import { Component, ElementRef, Inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ObservationsService } from '../../../../services/Observations/observations.service';
import {
  FormControl,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { ObservationDetailsComponent } from '../../../observations-manager/observation-details/observation-details.component';
import { NgxSpinnerService } from 'ngx-spinner';
import { GlobalService } from '../../../../services/global.service';
import moment, { Moment } from 'moment';
import { MatDatepicker } from '@angular/material/datepicker';
import { TranslocoService } from '@ngneat/transloco';
import { GStatic } from '../../../../services/GStatic';
import { GAnalyticsService } from '../../../../services/g-analytics.service';
import { Project360Service } from '../../../p-dashboard/project360.service';
import { ActiveMember } from '../../../../models/members.model';
import {
  Subject,
  Subscription,
  takeUntil,
  BehaviorSubject,
  Observable,
  firstValueFrom,
} from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import {
  ConfirmDialogComponent,
  ConfirmDialogModel,
} from '../../../../shared/components/confirm-dialog/confirm-dialog.component';
import { CreateACCIssuesDialogComponent } from '../../../p-dashboard/observation-management/create-acc-issues-dialog/create-acc-issues-dialog.component';
import { Bim360Service } from '../../../bim360/bim360.service';
import { ObservationDetails, ObservationForm } from '../../../../models/observations.model';
import { AccessLevel } from '../../../../core/user/user.model';
import { UserService } from 'app/core/user/user.service';
import { ObservationCategory, ObservationCategoryOption } from 'app/models/observations.model';
import Quill from 'quill';

// Custom Fields imports
import { CustomFieldRendererService } from '../../../../services/custom-field-renderer.service';
import { CustomField } from '../../../../models/custom-fields.model';
import { ObservationCustomFieldsService } from '../../../../services/observation-custom-fields.service';
import { QuillEditorService, EditorConfig } from '../../../../services/quill-editor.service';
import { appendObservationHashtagsToFormData } from '../../../../shared/utils/observation-form.utils';
import { getUploadPlaceholderImage } from 'app/shared/utils/branded-placeholder.util';

@Component({
  selector: 'app-observation-details-popup',
  templateUrl: './floor-observation-details-popup.component.html',
  styleUrls: ['./floor-observation-details-popup.component.scss'],
})
export class FloorObservationDetailsPopupComponent implements OnInit, OnDestroy {
  privacy: any;
  fileOptions = {
    maxFilesLimit: 10,
    maxFileSize: 200,
    allowedExtensions: [],
  };
  attachments = [];
  attachmentsError: string | null = null;
  observationHashtags: string[] = [];
  observationCategories: ObservationCategory[] = [];
  observationChildCategories: ObservationCategory[] = [];
  observationMembers: ActiveMember[] = [];
  formattedDate: string = '';
  observation = [];
  currentDueDate = new UntypedFormControl();
  observationForm!: ObservationForm;
  screenShotImage: any;
  observationDetails: ObservationDetails;
  isLoading = true;
  selectedType: boolean = true;
  observationSignificances = [];
  observationTypes = [];
  observationCodes = [];
  configurableFields: any[] = [];
  private observationCategoriesRequestId = 0;
  // @ViewChild('screenshot') screenshot: any;
  @ViewChild('fileAttached')
  fileAttached: ElementRef;
  @Input() minDate: Date;
  @ViewChild(MatDatepicker)
  picker: MatDatepicker<Moment>;
  setDate: Date;
  public fileToUpload: any;

  searchControl = new FormControl('');
  filteredUsers: ActiveMember[];
  protected _onDestroy = new Subject<void>();

  get maxQuillDescriptionLength(): number | null {
    const observationType = this.observationForm?.get('type')?.value;
    if (!observationType) {
      return null;
    }
    return this.quillEditorService.getDescriptionTextLimits(this.getEditorConfig());
  }

  get maxQuillRecommendedActionLength(): number | null {
    const observationType = this.observationForm?.get('type')?.value;
    if (!observationType) {
      return null;
    }
    return this.quillEditorService.getRecommendedActionTextLimits(this.getEditorConfig());
  }

  get screenshotPlaceholderPath(): string {
    return getUploadPlaceholderImage(this.nwc_client);
  }

  private getEditorConfig(): EditorConfig {
    return {
      nwcClient: this.nwc_client,
      observationType: this.observationForm.get('type')?.value || '',
      form: this.observationForm,
      observationCustomFields: this.observationCustomFields,
    };
  }
  // Get title max length based on client type
  get titleMaxLength(): number {
    return this.nwc_client ? 128 : 200;
  }

  private isEditorReady(editor: Quill): boolean {
    return editor && typeof editor.getText === 'function';
  }

  private isComponentReady(): boolean {
    return this.observationForm && !this.isLoading;
  }
  quillConfig = {
    toolbar: [
      ['bold'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      // [{ align: [] }],
      // [{ direction: 'rtl' }],
      ['clean'],
    ],
  };

  // Compare function for mat-select to match string/number ids
  compareWithId = (a: any, b: any): boolean => {
    return a !== undefined && a !== null && b !== undefined && b !== null
      ? String(a) === String(b)
      : a === b;
  };

  popupSubscription: Subscription;

  // Custom Fields Properties
  customFields$ = new BehaviorSubject<CustomField[]>([]);
  isLoadingCustomFields$ = new BehaviorSubject<boolean>(false);

  // Custom Fields for Viewing (separate from creating/editing)
  viewCustomFields$ = new BehaviorSubject<CustomField[]>([]);
  viewIsLoadingCustomFields$ = new BehaviorSubject<boolean>(false);
  private viewCustomFieldsSubscription?: Subscription;
  private viewCustomFieldsLoadVersion = 0;

  nwc_client: boolean = false;

  // Quill editor instances - populated via onEditorCreated callback
  descriptionEditor: Quill | null = null;
  recommendedActionEditor: Quill | null = null;

  publishObservation() {
    this.isLoading = true;
    const body = {
      project_id: this._project.id,
      observation_id: this.data.observation_id,
      is_draft: 'false',
    };
    this.observationsService.updateObservation(body).subscribe(
      (res) => {
        this.isLoading = false;
        this._toaster.success(this.translateService.translate('Observation successfully updated.'));
        this.dialogRef.close(true);
      },
      (error) => {
        this.isLoading = false;
        error.error.errors.forEach((errorMessage: string) => {
          this._toaster.error(errorMessage);
        });
      },
    );
  }

  checkDisabled() {
    return (
      this.observationDetails?.categories.length === 0 ||
      !this.observationDetails?.title ||
      !this.observationDetails?.privacy ||
      (this.observationDetails?.observation_flag !== 'observation_on_document' &&
        !this.observationDetails?.screen_shot)
    );
  }

  protected readonly AccessLevel = AccessLevel;

  protected readonly Boolean = Boolean;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private observations: ObservationsService,
    private observationsService: ObservationsService,
    public dialog: MatDialog,
    private dialogRef: MatDialogRef<ObservationDetailsComponent>,
    private fb: UntypedFormBuilder,
    private _toaster: ToastrService,
    private spinner: NgxSpinnerService,
    private globalService: GlobalService,
    public translateService: TranslocoService,
    private googleServices: GAnalyticsService,
    public _project: Project360Service,
    public bimService: Bim360Service,
    private customFieldRenderer: CustomFieldRendererService,
    private observationCustomFields: ObservationCustomFieldsService,
    private userService: UserService,
    private quillEditorService: QuillEditorService,
  ) {
    // Detect backdrop click
    // dialogRef.backdropClick().subscribe(() => {
    // this.hasUnsavedChanges() ? this.openConfirmationDialog() : this.dialogRef.close();
    // });
    // Detect Escape key press
    // dialogRef.keydownEvents().subscribe((event) => {
    //   if (event.key === 'Escape') {
    // this.hasUnsavedChanges() ? this.openConfirmationDialog() : this.dialogRef.close();
    //   }
    // });
  }

  async ngOnInit(): Promise<void> {
    console.log('ngOnInit floor observation details popup');
    this.nwc_client = this._project.isNwcClientSync();
    this.fileOptions.allowedExtensions = this._project.allowedExtensions;
    // Initialize form after nwc_client is set
    this.initForm();

    console.log('popup floor');
    if (this.data.type === 'ADD') {
      await this.getObservationSignificancesAndTypes();
      await this.getProjectTeam();
      this.isLoading = false;

      // Initialize custom fields properties
      const customFieldsProps = this.observationCustomFields.initializeCustomFieldsProperties();
      this.customFields$ = customFieldsProps.customFields$;
      this.isLoadingCustomFields$ = customFieldsProps.isLoadingCustomFields$;

      // Setup custom fields for the default observation type
      this.setupCustomFields();

      // setTimeout(() => this.getBinaryScreenShotFile(), 0);
    }
    if (this.data.observation_id) {
      this.getObservationDetails(this.data.observation_id);
    }

    //Add search to users list
    this.searchControl.valueChanges
      .pipe(debounceTime(700), takeUntil(this._onDestroy))
      .subscribe((val) => {
        if (val.toString().length > 0) {
          this.filteredUsers = this.observationMembers.filter(
            (user) =>
              user.name.toLowerCase().includes(val.toLowerCase()) ||
              user.email.toLowerCase().includes(val.toLowerCase()),
          );
        } else if (val.toString().length === 0) {
          this.filteredUsers = [...this.observationMembers];
        }
      });

    // Disable observation type when custom fields are loading
    this.isLoadingCustomFields$.pipe(takeUntil(this._onDestroy)).subscribe((isLoading) => {
      const typeControl = this.observationForm.get('type');
      if (isLoading) {
        typeControl?.disable();
      } else {
        typeControl?.enable();
      }
    });

    // Watch for description control status changes to disable/enable Quill editor
    this.observationForm
      .get('description')
      ?.statusChanges.pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.updateQuillEditorState();
      });

    this.popupSubscription = this.bimService.popupClosed$.subscribe(async () => {
      await this.bimService.getBim360Hubs();
      if (this.bimService.isLoggedIn) {
        const dialogRef = this.dialog.open(CreateACCIssuesDialogComponent, {
          width: '450px',
          data: {
            observationDetails: this.observationDetails,
          },
        });
        dialogRef.afterClosed().subscribe((res) => {
          this.dialog.closeAll();
        });
      }
    });
  }

  initForm(): void {
    this._setMinDate();
    const formControls: any = {
      project_id: [this.data.project_id, Validators.required],
      report_id: [this.data.report_id, Validators.required],
      floor_id: [this.data.floor_id, Validators.required],
      point_id: [this.data.point_id],
      location_x: [this.data.location_x, Validators.required],
      location_y: [this.data.location_y, Validators.required],
      title: [null, Validators.required],
      description: [null, this.nwc_client ? this.descriptionRequiredValidator() : null],
      recommended_corrective_action: [null],
      screen_shot: [null, Validators.required],
      categories: [this.nwc_client ? null : [], Validators.required],
      child_categories: [this.nwc_client ? null : [], this.subcategoryRequiredValidator()],
      users: [null, this.nwc_client ? null : Validators.required],
      observation_flag: ['observation_on_floor_plan'],
      code: ['none'], //OBS code with default value N/A => none
      nwc_client: [this.nwc_client],
    };

    // Conditionally add fields based on configurable_fields visibility
    if (this.isFieldVisible('type')) {
      formControls.type = [null, Validators.required];
    }
    if (this.isFieldVisible('privacy')) {
      formControls.privacy = ['public', Validators.required];
    }
    if (this.isFieldVisible('significance')) {
      formControls.significance = [null];
    }
    if (this.isFieldVisible('due_date')) {
      formControls.due_date = [null];
    }
    if (this.isFieldVisible('code')) {
      formControls.code = ['none']; //OBS code with default value N/A => none
    }

    this.observationForm = this.fb.group(formControls);
  }

  /**
   * Check if a field is visible based on configurable_fields
   * Returns true if field is visible, false if hidden or not configured
   */
  isFieldVisible(fieldName: string): boolean {
    if (!this.configurableFields || this.configurableFields.length === 0) {
      // If no configurable_fields, show all fields by default
      return true;
    }
    const fieldConfig = this.configurableFields.find((field) => field.attribute === fieldName);
    // If field is not in configurable_fields, show it by default
    // If field is in configurable_fields, use its visible property
    return fieldConfig ? fieldConfig.visible !== false : true;
  }

  /**
   * Remove hidden fields from the form based on configurable_fields
   */
  removeHiddenFieldsFromForm(): void {
    if (!this.observationForm || !this.configurableFields || this.configurableFields.length === 0) {
      return;
    }

    this.configurableFields.forEach((fieldConfig) => {
      if (fieldConfig.visible === false && this.observationForm.get(fieldConfig.attribute)) {
        this.observationForm.removeControl(fieldConfig.attribute);
      }
    });
  }

  getDate(value: any): void {
    this.setDate = new Date(value);
    const currentUserTime = moment(value).unix();
    const currentTimePlusHour = moment().add(0.99, 'hours').unix();
    if (currentUserTime <= currentTimePlusHour) {
      this._toaster.warning(
        this.translateService.translate(
          'Due time must be at least more than one hour from current time!',
        ),
      );
      this.currentDueDate = new UntypedFormControl('');
    } else {
      this.formattedDate = this.globalService.correctingTimeZone(value);
    }
  }

  resetPicker(): void {
    const message = this.translateService.translate('No due date is set');
    this.currentDueDate.setValue(message);
    this.formattedDate = '';
  }

  async getObservationCategories(): Promise<void> {
    const currentObservationType = this.observationForm.get('type')?.value;
    const requestId = ++this.observationCategoriesRequestId;

    try {
      const res: any = await firstValueFrom(
        this.observations.observationCategories(currentObservationType),
      );
      if (
        requestId !== this.observationCategoriesRequestId ||
        currentObservationType !== this.observationForm.get('type')?.value
      ) {
        return;
      }

      this.observationCategories = this.observationCustomFields.normalizeObservationCategories(
        res?.observation_categories,
      );
      this.observationCustomFields.syncSelectedObservationCategories(
        this.observationForm,
        this.observationCategories,
        this.nwc_client,
      );
      const categoriesOptions: ObservationCategoryOption[] =
        this.observationCustomFields.buildObservationCategoryOptions(
          this.observationCategories,
          this.translateService.getActiveLang(),
        );
      this.observationForm.metadata = {
        ...this.observationForm.metadata,
        categories: categoriesOptions,
      };

      // Update child categories based on current selected category
      this.updateChildCategories();
    } catch (error) {
      console.error('Error fetching observation categories:', error);
    }
  }

  updateChildCategories(): void {
    const selectedCategories = this.observationForm.get('categories')?.value;
    this.observationChildCategories = this.observationCustomFields.getObservationChildCategories(
      this.observationCategories,
      selectedCategories,
      this.nwc_client,
    );

    // Update validation for child categories after updating the available options
    this.updateChildCategoryValidation();
  }

  onCategorySelectionChange(): void {
    this.observationCustomFields.resetObservationCategoryState(
      this.observationForm,
      this.nwc_client,
    );
    this.updateChildCategories();
  }

  shouldShowSubcategoryField(): boolean {
    return this.observationCustomFields.shouldShowObservationSubcategoryField(
      this.observationForm,
      this.observationChildCategories,
      this.nwc_client,
      this.observationCategories,
    );
  }

  /**
   * Custom validator for child_categories that makes it required when subcategories are available
   */
  subcategoryRequiredValidator() {
    return (control: UntypedFormControl) => {
      // If there are no child categories available, no validation needed
      if (!this.observationChildCategories || this.observationChildCategories.length === 0) {
        return null;
      }

      // If child categories are available, the field is required
      const value = control.value;
      if (this.nwc_client) {
        // Single selection mode
        if (!value || value === null || value === undefined) {
          return { subcategoryRequired: true };
        }
      } else {
        // Multiple selection mode
        if (!value || !Array.isArray(value) || value.length === 0) {
          return { subcategoryRequired: true };
        }
      }

      return null;
    };
  }

  /**
   * Update child category validation when categories change
   */
  updateChildCategoryValidation(): void {
    const childCategoriesControl = this.observationForm.get('child_categories');
    if (childCategoriesControl) {
      // Re-apply the validator
      childCategoriesControl.setValidators(this.subcategoryRequiredValidator());
      childCategoriesControl.updateValueAndValidity();
    }
  }

  /**
   * Custom validator for description field that handles Quill editor HTML content
   * Validates that the description is not empty or whitespace-only
   */
  descriptionRequiredValidator() {
    return (control: UntypedFormControl) => {
      if (!this.nwc_client) {
        return null; // Not required for non-NWC clients
      }

      const value = control.value;

      // Handle null/undefined
      if (!value || value === null || value === undefined) {
        return { descriptionRequired: true };
      }

      // Strip HTML tags and extract plain text
      const textContent =
        typeof value === 'string'
          ? value.replace(/<[^>]*>/g, '').trim()
          : String(value)
              .replace(/<[^>]*>/g, '')
              .trim();

      // Check if empty after stripping HTML and trimming whitespace
      if (textContent === '' || textContent.length === 0) {
        return { descriptionRequired: true };
      }

      return null;
    };
  }

  async getObservationSignificancesAndTypes(): Promise<void> {
    try {
      console.log('getObservationSignificancesAndTypes');
      const res: any = await this.observationsService
        .observationSignificancesAndTypes()
        .toPromise();

      // Get all observation types and filter them based on current observation and client type
      this.observationTypes = res?.data?.observation_types || [];

      this.observationSignificances = res?.data?.observation_significance || [];
      this.observationCodes = res?.data?.observation_code?.sort((a, b) => a.order - b.order) || [];

      // Store configurable fields from API response
      this.configurableFields = res?.data?.configurable_fields || [];

      // Remove hidden fields from form after getting configurable_fields
      this.removeHiddenFieldsFromForm();

      const defaultType = this.observationTypes.find((item) => item.order === 1);
      if (defaultType) {
        this.observationForm.get('type')?.setValue(defaultType.value || null);
        this.setupCustomFields();
        this.getObservationCategories();
      } else {
        console.warn('No default observation type found with order === 1');
      }
      const defaultSignificance = this.observationSignificances.find((item) => item.order === 0);
      if (defaultSignificance) {
        this.observationForm.get('significance')?.setValue(defaultSignificance.value || null);
      }
    } catch (error) {
      console.error('Error fetching observation significances and types:', error);
    }
  }

  async getProjectTeam(): Promise<void> {
    try {
      const res = await this.observationsService.projectActiveMembers(this._project.id).toPromise();
      this.observationMembers = res.data;
      this.filteredUsers = this.observationMembers;
    } catch (error) {
      this._toaster.error(
        error || this.translateService.translate('Something went wrong, please try again.'),
      );
    }
  }

  /**
   * Handle type change from selectionChange event
   * This method is called when the user selects a different observation type
   */
  getType(type: string): void {
    this.attachmentsError = null;

    if (!type) {
      this.customFields$.next([]);
      this.isLoadingCustomFields$.next(false);
      return;
    }

    GStatic.log('type: ' + type);

    if (type !== 'note') {
      this.googleServices.setEvent(
        'AddNewObservation_SelectObservationType_project',
        'Viewer Interactivity',
      );
      this.selectedType = true;
    } else {
      this.googleServices.setEvent(
        'AddNewObservation_SelectNoteType_project',
        'Viewer Interactivity',
      );
      this.formattedDate = '';
      this.selectedType = false;
    }

    this.observationCustomFields.resetObservationCategoryState(
      this.observationForm,
      this.nwc_client,
      this.nwc_client,
    );
    this.observationChildCategories = [];
    this.updateChildCategoryValidation();

    // Handle observation type change (custom fields and categories)
    // API will be called and we handle empty/error responses gracefully
    this.observationCustomFields.handleObservationTypeChange(
      type,
      this.observationForm,
      this.customFields$,
      this.isLoadingCustomFields$,
      undefined, // onCategoriesLoaded
      (error) => {
        // Handle custom fields API error gracefully
        console.warn('Custom fields API error (non-critical):', error);
        // Form remains usable without custom fields
      },
      this.nwc_client, // Pass NWC client flag to preserve categories for non-NWC clients
    );

    // Load categories for the selected observation type
    this.getObservationCategories();

    // force editor length
    this.forceEditorLength();
  }

  /**
   * Setup custom fields for the current observation type
   * Handles empty responses and errors gracefully
   */
  setupCustomFields(): void {
    console.log('setupCustomFields');
    this.observationCustomFields
      .setupCustomFields(
        this.observationForm.get('type')?.value,
        this.observationForm,
        this.customFields$,
        this.isLoadingCustomFields$,
        (error) => {
          // Handle custom fields API error gracefully
          console.warn('Custom fields API error (non-critical):', error);
          // The form remains usable without custom fields
          // Ensure loading state is reset
          this.isLoadingCustomFields$.next(false);
        },
      )
      .subscribe();
  }

  /**
   * Get custom field values from the form
   * Returns empty object if custom fields are not available or empty
   */
  getCustomFieldValues(): any {
    if (!this.customFields$ || !this.customFields$.value || this.customFields$.value.length === 0) {
      return {};
    }

    return this.observationCustomFields.getCustomFieldValues(
      this.observationForm,
      this.customFields$.value,
    );
  }

  isDateValue(value: any): boolean {
    return this.observationCustomFields.isDateValue(value);
  }

  onObservationHashtagsChange(tags: string[]): void {
    this.observationHashtags = tags;
  }

  onSubmit(isDraft: boolean): void {
    if (this.isLoadingCustomFields$.value) {
      return;
    }

    if (this.observationForm.invalid && !isDraft) {
      this.observationForm.markAllAsTouched();
    }

    if (!this.observationForm.valid && !isDraft) {
      return;
    }

    if (!this.validateAttachmentsBeforeSubmit(isDraft)) {
      return;
    }

    GStatic.log('selectedType' + this.selectedType);
    this.isLoading = true;
    const FORM_BODY = new FormData();

    // Get form values including disabled controls
    const formValues = this.observationForm.getRawValue();

    // Process form data including custom fields (handles empty array gracefully)
    const customFieldsValue = this.customFields$?.value || [];
    this.observationCustomFields.processFormDataForSubmission(
      this.observationForm,
      FORM_BODY,
      customFieldsValue,
    );

    FORM_BODY.append('screen_shot', this.fileToUpload);
    // Handle category selection (single or multiple based on nwc_client)
    if (formValues.categories) {
      if (this.nwc_client) {
        // Single selection - ensure it's sent as a single value
        const categoryValue = Array.isArray(formValues.categories)
          ? formValues.categories[0]
          : formValues.categories;
        FORM_BODY.append('categories[]', categoryValue);
      } else {
        // Multiple selection
        formValues.categories.forEach((categoryId: number) => {
          FORM_BODY.append('categories[]', categoryId.toString());
        });
      }
    }
    // Handle child category selection (single or multiple based on nwc_client)
    if (formValues.child_categories) {
      if (this.nwc_client) {
        // Single selection - ensure it's sent as a single value
        const subcategoryValue = Array.isArray(formValues.child_categories)
          ? formValues.child_categories[0]
          : formValues.child_categories;
        FORM_BODY.append('subcategories[]', subcategoryValue);
      } else {
        // Multiple selection
        formValues.child_categories.forEach((categoryId: number) => {
          FORM_BODY.append('subcategories[]', categoryId.toString());
        });
      }
    }
    formValues.users?.map((user: any) => FORM_BODY.append('users[]', user));
    FORM_BODY.append('due_date', this.formattedDate);
    this.attachments.map((file) => FORM_BODY.append('attached[]', file));

    //add draft flag
    FORM_BODY.append('is_draft', isDraft.toString());

    this.observationsService
      .createObservation(appendObservationHashtagsToFormData(FORM_BODY, this.observationHashtags))
      .subscribe(
        (res) => {
          const message = res.message || this.translateService.translate('Added Successfully');
          if (!this.data.is_observation_creation_enabled) {
            this._toaster.info(message, null, { disableTimeOut: true });
          } else {
            this.globalService.showTopMsg('success', message);
          }
          this.isLoading = false;
          this.dialogRef.close({ ...formValues, id: res.observation_id });
        },
        (error) => {
          error.error.errors.forEach((errorMessage: string) => {
            this._toaster.error(errorMessage);
          });
          this.isLoading = false;
        },
      );
  }

  get requiresAttachmentsForCurrentType(): boolean {
    return this.observationCustomFields.requiresAttachmentForCreation(
      this.observationForm?.value?.type,
      this.nwc_client,
    );
  }

  get isMissingRequiredAttachments(): boolean {
    return this.requiresAttachmentsForCurrentType && this.attachments.length === 0;
  }

  onAttachmentsChanged(files: File[]): void {
    this.attachments = files;
    this.attachmentsError = null;
  }

  private validateAttachmentsBeforeSubmit(isDraft: boolean): boolean {
    const validation = this.observationCustomFields.validateAttachmentsForCreation(
      this.observationForm?.getRawValue()?.type ?? this.observationForm?.value?.type,
      this.nwc_client,
      this.attachments,
      isDraft,
    );

    if (!validation.valid) {
      this.attachmentsError = validation.errorKey;
      return false;
    }

    this.attachmentsError = null;
    return true;
  }

  getObservationDetails(observationID: string): void {
    this.observationsService.getObservationDetails(observationID).subscribe((res: any) => {
      this.isLoading = false;
      this.observationDetails = res?.observation_details;

      // Store configurable fields from API response
      if (res?.observation_details?.configurable_fields) {
        this.observationDetails.configurable_fields = res.observation_details.configurable_fields;
      }

      if (this.observationDetails.due_date == null) {
        this.observationDetails.due_date = 'N/A'.toUpperCase();
      }
      this.observationDetails.categories_str = this.observationDetails?.categories
        .map((cat: any) => cat.category_name_en)
        .join(',');
      this.observationDetails.user_assignees_str = this.observationDetails?.user_assignees
        .map((user: any) => user.user_name)
        .join(',');

      // Load custom fields for the observation type
      this.setupViewCustomFields();
    });
  }

  /**
   * Setup custom fields for viewing observation details
   */
  setupViewCustomFields(): void {
    const loadVersion = ++this.viewCustomFieldsLoadVersion;
    this.viewCustomFieldsSubscription?.unsubscribe();

    // Early return for note type or empty observation type
    if (!this.observationDetails?.type || this.observationDetails?.type === 'note') {
      this.viewCustomFields$.next([]);
      this.viewIsLoadingCustomFields$.next(false);
      return;
    }

    this.viewIsLoadingCustomFields$.next(true);

    const customFieldsObservable = this.customFieldRenderer.loadCustomFieldsForObservationType(
      this.observationDetails?.type,
      this.fb.group({ project_id: [this._project.id] }),
      async (fields: CustomField[]) => {
        if (loadVersion !== this.viewCustomFieldsLoadVersion) {
          return;
        }

        if (fields && fields.length > 0) {
          const fieldsWithValues = fields.map((field) => ({
            ...field,
            currentValue:
              this.observationDetails?.custom_fields?.find((f) => f.name === field.name)?.default ??
              null,
          }));

          this.viewCustomFields$.next(fieldsWithValues);
          this.viewIsLoadingCustomFields$.next(false);
        } else {
          this.viewCustomFields$.next([]);
          this.viewIsLoadingCustomFields$.next(false);
        }
      },
      (error) => {
        if (loadVersion !== this.viewCustomFieldsLoadVersion) {
          return;
        }

        console.error('Error loading custom fields from API:', error);
        this.viewCustomFields$.next([]);
        this.viewIsLoadingCustomFields$.next(false);
      },
      // Existing observation context (floor details popup) — let the BE mark
      // creation-locked fields as read_only.
      this.observationDetails?.id,
    );

    this.viewCustomFieldsSubscription = customFieldsObservable.subscribe();
  }

  /**
   * Handle custom field updates from shared observation details component
   */
  editObservationCustomFields($event: any): void {
    const formData = new FormData();
    formData.append('project_id', this._project.id);
    formData.append('observation_id', this.observationDetails.id);

    // Add custom field values
    Object.keys($event).forEach((key) => {
      const value = $event[key];
      if (value !== null && value !== undefined) {
        if (this.observationCustomFields.isDateValue(value)) {
          formData.append(key, this.customFieldRenderer.formatDateForApi(value));
        } else {
          formData.append(key, value);
        }
      }
    });

    this.observationsService.updateObservation(formData).subscribe(
      () => {
        this._toaster.success(
          this.translateService.translate('Custom fields updated successfully'),
        );
        this.getObservationDetails(this.observationDetails.id);
      },
      (error) => {
        this._toaster.error(
          this.translateService.translate(error.error.message || 'Failed to update custom fields'),
        );
      },
    );
  }

  fileChangeEvent(event: any): void {
    GStatic.log('sada');
    const file = event.target?.files[0];
    this.fileAttached.nativeElement.value = '';
    if (!file) {
      return;
    } else {
      const mimeType = file.type;
      if (mimeType.match(/image\/*/) == null) {
        return;
      }
      const reader = new FileReader();
      const img = new Image();
      this.fileToUpload = file;

      img.onload = () => {};

      if (file && typeof file === 'string') {
        img.src = file;
        this.screenShotImage = file;
      } else {
        reader.readAsDataURL(file);
        reader.onload = (_event) => {
          this.screenShotImage = reader.result;
          img.src = this.screenShotImage;
        };
      }
    }
  }

  removeImg(): void {
    this.observationForm.get('screen_shot').setValue('');
    this.fileToUpload = undefined;
    this.screenShotImage = undefined;
  }

  checkMobile(): boolean {
    const ua = window.navigator.userAgent;
    if (
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i.test(ua)
    )
      return true;
    else return false;
  }

  getObservationType(): boolean {
    const type = this.observationForm.get('type').value;
    if (type !== 'note') {
      this.observationForm.get('users').updateValueAndValidity();
      this.observationForm.get('users').setValidators([Validators.required]);

      return true;
    } else {
      this.observationForm.get('users').clearValidators();
      this.observationForm.get('users').updateValueAndValidity();

      return false;
    }
  }

  editObservationPrivacy($event: any) {
    const body = {
      project_id: this._project.id,
      observation_id: this.observationDetails.id,
      privacy: $event.privacy,
    };
    this.observationsService.updateObservation(body).subscribe(
      (res) => {
        this.getObservationDetails(this.observationDetails.id);
        this.dialogRef.close(true);
      },
      (error) => {
        error.error.errors.forEach((errorMessage: string) => {
          this._toaster.error(errorMessage);
        });
      },
    );
  }

  editObservationTitle($event: any) {
    const body = {
      project_id: this._project.id,
      observation_id: this.observationDetails.id,
      title: $event.title,
    };
    this.observationsService.updateObservation(body).subscribe(
      (res) => {
        this.getObservationDetails(this.observationDetails.id);
        this.dialogRef.close(true);
      },
      (error) => {
        error.error.errors.forEach((errorMessage: string) => {
          this._toaster.error(errorMessage);
        });
      },
    );
  }

  editObservationCategories($event: any) {
    const body = {
      project_id: this._project.id,
      observation_id: this.observationDetails.id,
      categories: $event.categories,
      subcategories: $event.subcategories || [],
    };
    this.observationsService.updateObservation(body).subscribe(
      (res) => {
        this.getObservationDetails(this.observationDetails.id);
        // this.dialogRef.close(true);
      },
      (error) => {
        error.error.errors.forEach((errorMessage: string) => {
          this._toaster.error(errorMessage);
        });
      },
    );
  }

  editObservationAttachments($event: any) {
    let body = {};
    if ($event.attached_files.length > 0) {
      const formData = new FormData();
      formData.append('project_id', this._project.id);
      formData.append('observation_id', this.observationDetails.id);
      for (const file of $event.attached_files) {
        formData.append('attached[]', file);
      }
      body = formData;
    } else {
      const formData = new FormData();
      formData.append('project_id', this._project.id);
      formData.append('observation_id', this.observationDetails.id);
      formData.append('attached[]', '');
      body = formData;
    }

    this.observationsService.updateObservation(body).subscribe(
      (res) => {
        this.getObservationDetails(this.observationDetails.id);
        // this.dialogRef.close(true);
      },
      (error) => {
        error.error.errors.forEach((errorMessage: string) => {
          this._toaster.error(errorMessage);
        });
      },
    );
  }

  editObservationDescription($event: any) {
    const body = {
      project_id: this._project.id,
      observation_id: this.observationDetails.id,
      description: $event.description,
    };
    this.observationsService.updateObservation(body).subscribe(
      (res) => {
        this.getObservationDetails(this.observationDetails.id);
        // this.dialogRef.close(true);
      },
      (error) => {
        error.error.errors.forEach((errorMessage: string) => {
          this._toaster.error(errorMessage);
        });
      },
    );
  }

  editObservationRecommendedAction($event: any) {
    const body = {
      project_id: this._project.id,
      observation_id: this.observationDetails.id,
      recommended_corrective_action: $event.recommended_corrective_action,
    };
    this.observationsService.updateObservation(body).subscribe(
      (res) => {
        this.getObservationDetails(this.observationDetails.id);
        // this.dialogRef.close(true);
      },
      (error) => {
        error.error.errors.forEach((errorMessage: string) => {
          this._toaster.error(errorMessage);
        });
      },
    );
  }

  editObservationType($event: any) {
    const body = {
      project_id: this._project.id,
      observation_id: this.observationDetails.id,
      type: $event.type,
    };
    this.observationsService.updateObservation(body).subscribe(
      (res) => {
        this.getObservationDetails(this.observationDetails.id);
        // this.dialogRef.close(true);
      },
      (error) => {
        error.error.errors.forEach((errorMessage: string) => {
          this._toaster.error(errorMessage);
        });
      },
    );
  }

  editObservationStatus(status: any): void {
    const body = {
      project_id: this._project.id,
      observation_id: this.observationDetails.id,
      status: status,
    };
    this.observationsService.updateObservation(body).subscribe(
      async (res) => {
        await this._project.fetchObservations(this._project.currentPImg.id).catch((error) => {
          this._toaster.error(error.error.message);
        });
        this.dialogRef.close(true);
      },
      (error) => {
        error.error.errors.forEach((errorMessage: string) => {
          this._toaster.error(errorMessage);
        });
      },
    );
    GStatic.log(this.observationDetails.due_date);
  }

  editObservationSignificance(significance: any): void {
    const body = {
      project_id: this._project.id,
      observation_id: this.observationDetails.id,
      significance: significance.significance,
    };
    this.observationsService.updateObservation(body).subscribe(
      (res) => {
        this.getObservationDetails(this.observationDetails.id);
        this.dialogRef.close(true);
      },
      (error) => {
        error.error.errors.forEach((errorMessage: string) => {
          this._toaster.error(errorMessage);
        });
      },
    );
  }

  editObservationDueDate(date: any): void {
    const body = {
      project_id: this._project.id,
      observation_id: this.observationDetails.id,
      due_date: this.globalService.correctingTimeZone(date.date),
    };
    this.observationsService.updateObservation(body).subscribe(
      (res) => {
        this.getObservationDetails(this.observationDetails.id);
        // this.dialogRef.close(true);
      },
      (error) => {
        error.error.errors.forEach((errorMessage: string) => {
          this._toaster.error(errorMessage);
        });
      },
    );
  }

  private _setMinDate(): void {
    this.minDate = new Date();
    this.minDate.setUTCHours(0, 0, 0, 0);
  }

  async editObservationEvidence($event: any) {
    if ($event === 'delete') {
      this.getObservationDetails(this.observationDetails.id);
      await this._project.fetchObservations(this._project.currentPImg.id).catch((error) => {
        this._toaster.error(error.error.message);
      });
    } else {
      const formData = new FormData();
      formData.append('project_id', this._project.id);
      formData.append('observation_id', this.observationDetails.id);
      $event.evidence_file.forEach((file) => {
        if (file.name.endsWith('.kmz')) {
          const file2 = new File([file], file.name, {
            type: 'application/vnd.google-earth.kmz',
          });
          formData.append('evidence_file', file2);
        } else {
          formData.append('evidence_file', file);
        }
      });
      this.observationsService.updateObservation(formData).subscribe(
        async (res) => {
          this.getObservationDetails(this.observationDetails.id);
          await this._project.fetchObservations(this._project.currentPImg.id).catch((error) => {
            this._toaster.error(error.error.message);
          });
        },
        (error) => {
          // Error callback
          this._toaster.error(this.translateService.translate(error.error.message));
        },
      );
    }
  }

  editObservationCode($event: any) {
    this.isLoading = true;
    const body = {
      project_id: this._project.id,
      observation_id: this.observationDetails.id,
      code: $event.type,
    };
    this.observationsService.updateObservation(body).subscribe(async (res) => {
      this.getObservationDetails(this.observationDetails.id);
      await this._project.fetchObservations(this._project.currentPImg.id).catch((error) => {
        this._toaster.error(error.error.message);
      });

      this.isLoading = false;
    });
  }

  hasUnsavedChanges(): boolean {
    const excludedFields = ['type', 'privacy', 'observation_flag', 'code'];

    return Object.keys(this.observationForm.controls).some((key) => {
      const control = this.observationForm.controls[key];
      return (
        control.dirty &&
        control.value !== '' &&
        control.value !== null &&
        !excludedFields.includes(key)
      );
    });
  }

  openConfirmationDialog() {
    const formValues = this.observationForm.getRawValue();
    const dialogData = new ConfirmDialogModel(
      this.translateService.translate('Warning'),
      this.translateService.translate(`You have unsaved changes. What would you like to do?`),
      false,
      false,
      null,
      this.translateService.translate('Save Draft'),
      this.translateService.translate('Cancel'),
      '',
      false,
      [],
      this.translateService.translate('Keep Editing'),
      !(formValues.title && formValues.type),
      'heroicons_solid:exclamation',
    );

    const confirmDialog: MatDialogRef<ConfirmDialogComponent> = this.dialog.open(
      ConfirmDialogComponent,
      {
        maxWidth: '640px',
        minWidth: '300px',
        data: dialogData,
        disableClose: true,
      },
    );

    confirmDialog.afterClosed().subscribe((result) => {
      if (result) {
        result === true ? this.onSubmit(true) : null;
      } else {
        this.dialogRef.close(false);
      }
    });
  }

  async createACCIssue() {
    if (this.bimService.getAccessToken()) {
      await this.bimService.getBim360Hubs().then((res) => {
        if (res) {
          const dialogRef = this.dialog.open(CreateACCIssuesDialogComponent, {
            width: '450px',
            data: {
              observationDetails: this.observationDetails,
            },
          });
          dialogRef.afterClosed().subscribe((res) => {
            if (res) {
              this.dialogRef.close();
            }
          });
        } else {
          this.bimService.openBIMLoginPopup();
        }
      });
    } else {
      this.bimService.openBIMLoginPopup();
    }
  }

  isArabic() {
    let currentLang = localStorage.getItem('lang');
    return currentLang === 'ar';
  }

  onEditorCreated(quill: Quill, editorType?: 'description' | 'recommendedAction') {
    if (!editorType) return;

    // Store the editor reference based on type
    if (editorType === 'description') {
      this.descriptionEditor = quill;
      // Apply initial disabled state for description editor
      setTimeout(() => this.updateQuillEditorState(), 0);
    } else if (editorType === 'recommendedAction') {
      this.recommendedActionEditor = quill;
    }

    // Setup editor using the service
    this.quillEditorService.setupEditor(quill, editorType, this.getEditorConfig());
  }

  /**
   * Update Quill editor state based on form control disabled status
   * This ensures the visual appearance matches the disabled state
   */
  private updateQuillEditorState(): void {
    const descriptionControl = this.observationForm.get('description');

    // Check if both the editor and control exist, and the editor has the enable method
    if (
      this.descriptionEditor &&
      descriptionControl &&
      typeof this.descriptionEditor.enable === 'function'
    ) {
      const isDisabled = descriptionControl.disabled;
      this.descriptionEditor.enable(!isDisabled);

      // Add/remove visual disabled styling to match Material UI disabled state
      const editorContainer = this.descriptionEditor.container;
      if (editorContainer) {
        if (isDisabled) {
          editorContainer.style.opacity = '0.5';
          editorContainer.style.pointerEvents = 'none';
          editorContainer.style.cursor = 'not-allowed';
          // Don't change background color - keep the existing dark theme
        } else {
          editorContainer.style.opacity = '1';
          editorContainer.style.pointerEvents = 'auto';
          editorContainer.style.cursor = 'text';
        }
      }
    }
  }

  getDescriptionCharCount(): number | null {
    const observationType = this.observationForm?.get('type')?.value;
    if (!observationType) {
      return null;
    }
    return this.quillEditorService.getTextCharCount(
      this.descriptionEditor,
      'description',
      this.getEditorConfig(),
    );
  }

  getRecommendedActionCharCount(): number | null {
    const observationType = this.observationForm?.get('type')?.value;
    if (!observationType) {
      return null;
    }
    return this.quillEditorService.getTextCharCount(
      this.recommendedActionEditor,
      'recommendedAction',
      this.getEditorConfig(),
    );
  }

  forceEditorLength(): void {
    if (this.descriptionEditor) {
      // Re-setup editor to update event handlers with new limits
      this.quillEditorService.setupEditor(
        this.descriptionEditor,
        'description',
        this.getEditorConfig(),
      );
      this.quillEditorService.forceEditorLength(
        this.descriptionEditor,
        'description',
        this.getEditorConfig(),
      );
    }

    if (this.recommendedActionEditor) {
      // Re-setup editor to update event handlers with new limits
      this.quillEditorService.setupEditor(
        this.recommendedActionEditor,
        'recommendedAction',
        this.getEditorConfig(),
      );
      this.quillEditorService.forceEditorLength(
        this.recommendedActionEditor,
        'recommendedAction',
        this.getEditorConfig(),
      );
    }
  }

  ngOnDestroy() {
    if (this.popupSubscription) {
      this.popupSubscription.unsubscribe();
    }
    this.viewCustomFieldsSubscription?.unsubscribe();
    this._onDestroy.next();
    this._onDestroy.complete();

    // Safely complete custom fields observables if they exist
    if (this.customFields$) {
      this.customFields$.complete();
    }
    if (this.isLoadingCustomFields$) {
      this.isLoadingCustomFields$.complete();
    }
    if (this.viewCustomFields$) {
      this.viewCustomFields$.complete();
    }
    if (this.viewIsLoadingCustomFields$) {
      this.viewIsLoadingCustomFields$.complete();
    }
  }
}
